'use strict';
/*
 * Axiomi offline outbox — queues field-capture creates made while offline and
 * replays them idempotently when connectivity returns.
 *
 * Loaded as a classic script BEFORE api.js. api.js wires the real HTTP sender via
 * Outbox._bindHttp(fn) and calls Outbox.start(). On replay, each queued op is sent
 * with its opId as the `Idempotency-Key` header, so the server dedups (see
 * src/core/idempotency.js) — a create can never be duplicated even if the original
 * response was lost.
 *
 * Only append-only field creates are queued (hazards, safety observations, culture
 * walks). Edits are never queued. Photos ride inline in the JSON body (base64), so a
 * queued op is fully serialisable and lives in IndexedDB.
 *
 * The pure decision helpers (isQueueable / buildEntry / classifyOutcome / newOpId)
 * are exported for unit testing; the IndexedDB + flush machinery is browser-only.
 */
(function (root) {
  var DB_NAME = 'axiomi-outbox', STORE = 'ops', DB_VERSION = 1;
  var SYNC_TAG = 'axiomi-outbox'; // == public/sw.js SYNC_TAG — Background Sync tag (Phase 4)

  /* ---- pure, testable helpers (no browser APIs touched at import time) ---- */
  var QUEUEABLE = [
  { method: 'POST', re: /^\/api\/maintenance\/readings$/ },
  { method: 'POST', re: /^\/api\/maintenance\/work-orders\/[^/]+\/complete$/ },
  { method: 'POST', re: /^\/api\/quality\/inspections$/ },
    { method: 'POST', re: /^\/api\/hazards$/ },
    { method: 'POST', re: /^\/api\/checklists\/templates\/[^/]+\/submit$/ }, // field rounds in coverage shadows — offline-first (P0-2)
    { method: 'POST', re: /^\/api\/field\/observations$/ },
    { method: 'POST', re: /^\/api\/culture\/observations$/ },
    { method: 'POST', re: /^\/api\/culture\/walks$/ },
    { method: 'POST', re: /^\/api\/shift-handovers$/ }, // field handover at remote sites — offline-first
    { method: 'POST', re: /^\/api\/inbound\/vehicles$/ }, // gate arrival capture — coverage shadows at security cabins
  ];
  function isQueueable(method, path) {
    var p = String(path || '').split('?')[0];
    return QUEUEABLE.some(function (q) { return q.method === method && q.re.test(p); });
  }
  function newOpId() {
    if (root.crypto && root.crypto.randomUUID) return 'op-' + root.crypto.randomUUID();
    return 'op-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }
  function buildEntry(method, path, body, opId) {
    return {
      opId: opId || newOpId(), method: method, path: path,
      body: (body === undefined ? null : body),
      createdAt: new Date().toISOString(), tries: 0, state: 'pending', lastError: '',
    };
  }
  // Decide what to do with a flush attempt: 'done' (remove) | 'retry' (keep) | 'failed' (quarantine).
  function classifyOutcome(r) {
    r = r || {};
    if (r.networkError) return 'retry';                                   // offline / dropped
    if (r.ok) return 'done';                                              // 2xx
    var s = r.status || 0;
    if (s >= 400 && s < 500 && s !== 408 && s !== 429) return 'failed';   // permanent client error
    return 'retry';                                                       // 5xx / 408 / 429 / unknown
  }

  // Phase 4 capability probe — is the SW Background Sync API usable here? True only
  // on Chromium/Android; false on iOS Safari & friends, where we rely on the page
  // flush. Pure (env injected) so it is unit-testable under node --test.
  function supportsBackgroundSync(win) {
    return !!(win && win.SyncManager && win.navigator && win.navigator.serviceWorker);
  }

  var api = { isQueueable: isQueueable, newOpId: newOpId, buildEntry: buildEntry, classifyOutcome: classifyOutcome, supportsBackgroundSync: supportsBackgroundSync, SYNC_TAG: SYNC_TAG, QUEUEABLE: QUEUEABLE };

  /* ---- browser-only IndexedDB + flush machinery ---- */
  if (typeof window !== 'undefined' && window.indexedDB) {
    var _httpFn = null, _flushing = false, _timer = null;

    function openDB() {
      return new Promise(function (resolve, reject) {
        var req = window.indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = function () {
          var db = req.result;
          if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'opId' });
        };
        req.onsuccess = function () { resolve(req.result); };
        req.onerror = function () { reject(req.error); };
      });
    }
    function withStore(mode, fn) {
      return openDB().then(function (db) {
        return new Promise(function (resolve, reject) {
          var t = db.transaction(STORE, mode);
          var request = fn(t.objectStore(STORE));
          request.onsuccess = function () { resolve(request.result); };
          request.onerror = function () { reject(request.error); };
          t.onabort = function () { reject(t.error); };
        });
      });
    }
    function putEntry(e) { return withStore('readwrite', function (s) { return s.put(e); }).then(function () { return e; }); }
    function allEntries() { return withStore('readonly', function (s) { return s.getAll(); }).then(function (r) { return r || []; }); }
    function removeEntry(id) { return withStore('readwrite', function (s) { return s.delete(id); }); }
    function pendingList() { return allEntries().then(function (rows) { return rows.filter(function (e) { return e.state !== 'failed'; }); }); }
    function pendingCount() { return pendingList().then(function (rows) { return rows.length; }); }

    function emit(name, detail) { try { window.dispatchEvent(new CustomEvent(name, { detail: detail })); } catch (_) {} }

    // Phase 4 — ask the browser to wake the SW and drain the outbox even if the tab
    // is later closed (Chromium/Android only). Best-effort: the page-context flush
    // (online/focus/timer) stays the primary, cross-platform path regardless.
    function requestBackgroundSync() {
      try {
        if (!supportsBackgroundSync(window) || !navigator.serviceWorker) return;
        navigator.serviceWorker.ready.then(function (reg) {
          if (reg && reg.sync && reg.sync.register) return reg.sync.register(SYNC_TAG);
        }).catch(function () {});
      } catch (_) {}
    }

    function enqueue(method, path, body) {
      var entry = buildEntry(method, path, body);
      return putEntry(entry).then(function () {
        emit('outbox:queued', { opId: entry.opId, path: path });
        requestBackgroundSync();
        scheduleFlush(1500);
        return entry;
      });
    }

    function flush() {
      if (_flushing || !_httpFn) return Promise.resolve();
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return Promise.resolve();
      _flushing = true;
      return pendingList().then(function (rows) {
        rows.sort(function (a, b) { return String(a.createdAt).localeCompare(String(b.createdAt)); });
        var chain = Promise.resolve();
        rows.forEach(function (entry) {
          chain = chain.then(function () {
            return _httpFn(entry.method, entry.path, entry.body, { idempotencyKey: entry.opId, _replay: true })
              .then(function (data) {
                return removeEntry(entry.opId).then(function () { emit('outbox:synced', { opId: entry.opId, path: entry.path, response: data }); });
              })
              .catch(function (err) {
                var outcome = classifyOutcome({ networkError: !err || err.status === undefined, status: err && err.status });
                entry.tries += 1; entry.lastError = (err && err.message) || 'error';
                if (outcome === 'failed') {
                  entry.state = 'failed';
                  return putEntry(entry).then(function () { emit('outbox:failed', { opId: entry.opId, path: entry.path, error: entry.lastError }); });
                }
                // transient (offline again / 5xx) → keep and stop this pass
                return putEntry(entry).then(function () { return Promise.reject({ _stop: true }); });
              });
          });
        });
        return chain.catch(function (e) { if (!e || !e._stop) throw e; });
      }).then(
        function () { _flushing = false; return pendingCount().then(function (n) { emit('outbox:idle', { pending: n }); }); },
        function (e) { _flushing = false; throw e; }
      );
    }

    function scheduleFlush(delay) {
      if (_timer) clearTimeout(_timer);
      _timer = setTimeout(function () { _timer = null; flush().catch(function () {}); }, delay || 500);
    }

    api.enqueue = enqueue;
    api.flush = flush;
    api.list = allEntries;
    api.pendingList = pendingList;
    api.pendingCount = pendingCount;
    api.remove = removeEntry;
    api.requestBackgroundSync = requestBackgroundSync;
    api._bindHttp = function (fn) { _httpFn = fn; };
    api.start = function () {
      window.addEventListener('online', function () { scheduleFlush(300); });
      window.addEventListener('focus', function () { scheduleFlush(800); });
      setInterval(function () {
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
        pendingCount().then(function (n) { if (n > 0) flush().catch(function () {}); });
      }, 45000);
      scheduleFlush(2000); // initial drain shortly after load
      // Re-register a background sync for anything still queued from a prior session
      // (Chromium/Android). No-op elsewhere; the page flush above covers all platforms.
      pendingCount().then(function (n) { if (n > 0) requestBackgroundSync(); }).catch(function () {});
    };

    window.Outbox = api;
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof self !== 'undefined' ? self : this);
